/**
 * Shared layout for static legal pages (Privacy, Terms, Imprint, Cookies).
 *
 * Keeps each policy page focused on content. Pulls Navbar from the landing
 * design system so legal pages still feel like part of RentNova.
 *
 * Placeholders left in [BRACKETS] are for the operator to fill in once. They
 * map to real business identity that only the company can supply (legal name,
 * address, IČO, DIČ, court registration). This file deliberately does NOT
 * pretend those are filled in — they are visible until edited.
 */

import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import Footer from '../../components/Footer';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import './LegalLayout.css';

const LEGAL_NAV: { to: string; label: string }[] = [
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Service' },
  { to: '/cookies', label: 'Cookie Policy' },
  { to: '/imprint', label: 'Imprint' },
];

interface LegalLayoutProps {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}

export default function LegalLayout({ title, lastUpdated, children }: LegalLayoutProps) {
  const { pathname } = useLocation();

  return (
    <div className="legal-page">
      <header className="legal-page__header">
        <div className="container legal-page__header-inner">
          <Link to="/" className="legal-page__logo">
            <img src="/rentnova-mark.png" alt="" width={28} height={28} />
            <span>RentNova</span>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="container legal-page__container">
        <aside className="legal-page__sidebar" aria-label="Legal pages">
          <nav>
            <ul>
              {LEGAL_NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={pathname === item.to ? 'is-active' : ''}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <main className="legal-page__content">
          <h1>{title}</h1>
          <p className="legal-page__meta">Last updated: {lastUpdated}</p>
          {children}
          <p className="legal-page__back">
            <Link to="/">← Back to RentNova</Link>
          </p>
        </main>
      </div>

      <Footer />
    </div>
  );
}
